import dayjs from "dayjs";
import { sendSignal, WebAuthnError } from "@simplewebauthn/browser";
import type { Passkey } from "api/passkeys";
import storage from "utils/storage";

// "Added Oct 6, 2026 · last used Oct 9, 2026 · synced"
export const passkeyDetails = (passkey: Passkey): string =>
  [
    `Added ${dayjs(passkey.createdAt).format("MMM D, YYYY")}`,
    passkey.lastUsedAt
      ? `last used ${dayjs(passkey.lastUsedAt).format("MMM D, YYYY")}`
      : "never used",
    passkey.backedUp ? "synced" : null,
  ]
    .filter(Boolean)
    .join(" · ");

// The user closed the passkey prompt, or another ceremony replaced this one
// (e.g. the button press cancelling the autofill request). Not worth an error.
export const isPasskeyCancel = (err: unknown): boolean => {
  if (err instanceof WebAuthnError) {
    if (err.code === "ERROR_CEREMONY_ABORTED") return true;
    const cause = err.cause as Error | undefined;
    return cause?.name === "NotAllowedError" || cause?.name === "AbortError";
  }
  const name = (err as Error | undefined)?.name;
  return name === "NotAllowedError" || name === "AbortError";
};

// Tell the device a passkey no longer works for Riivet so it stops offering
// it. Best effort: browsers without the Signal API just keep showing it.
export const forgetPasskeyOnDevice = async (
  rpId: string,
  credentialId: string,
) => {
  try {
    await sendSignal({
      signalName: "unknownCredential",
      rpID: rpId,
      credentialID: credentialId,
    });
  } catch {
    // Unsupported browser or rejected signal; nothing else to do.
  }
};

// This browser has added or used a passkey, so the sign-in page shows the
// passkey button even during the staff-only rollout.
export const rememberPasskeyBrowser = () => storage.set("passkey-hint", "1");
export const browserUsedPasskey = () => storage.get("passkey-hint") === "1";
